import React, { useEffect, useRef, useState } from 'react';
import { X, Send, AlertTriangle } from 'lucide-react';

// Tek bacak emri — tarayıcı satırındaki bir borsaya doğrudan emir gönderir.
//
// Yön BELİRSİZ DEĞİL: Long kolonundan açılırsa o borsada ALIM, Short kolonundan
// açılırsa SATIM. Fiyat da uygulanabilir taraftan gelir (long → ask, short → bid),
// çünkü karşı tarafı sen geçiyorsun.
//
// Akış: bu popup'la ilk bacağı at, dolduğunda grup kartındaki "Karşı bacağı
// tamamla" ile ikinci bacağı otomatik süpürt.

export interface LegOrderPrefill {
  botId: string;
  botName: string;
  symbol: string;
  side: 'long' | 'short';
  price: number;        // uygulanabilir fiyat (long→ask, short→bid)
  availQty: number;     // o fiyattaki görünen miktar (0 = bilinmiyor)
  makerFee?: number;    // %
  takerFee?: number;    // %
  feeSource?: string;
  leverage?: number;
  // ── Kapatma kipi (grup kartındaki bacak tuşları) ────────────────────────────
  closing?: boolean;        // true → reduce_only, "kapat" dili, pozisyon özeti
  groupNo?: number;         // dolum bu gruba otomatik bağlanır
  openSide?: 'long' | 'short';   // kapatılan pozisyonun yönü (side bunun TERSİ)
  openQty?: number;         // grup muhasebesindeki açık miktar
  posSide?: string;         // borsadaki GERÇEK pozisyon (uyuşmazlık uyarısı için)
  posSize?: number;
  maker?: boolean;          // hangi tuşla açıldı — popup o kipte başlar
  ageS?: number;            // fiyatın yaşı (sn) — 0 canlı defter, -1 bilinmiyor
  // ── Arb bacağı açarken (tarayıcı satırındaki ⊳) ─────────────────────────────
  base?: string;            // çıplak coin adı
  counterBotId?: string;    // karşı bacağın borsası
  counterSymbol?: string;   // karşı bacağın o borsadaki sembolü
}

interface Props {
  dark?: boolean;
  prefill: LegOrderPrefill;
  sendWs?: (m: any) => void;
  /** Dolum onaylanınca "karşı bacağı aç" için — grup no ve gerçekleşen miktarla. */
  onHedge?: (a: { groupNo: number; quantity: number; refPrice: number }) => void;
  sendForBot: (m: any, botId: string) => void;
  subscribe: (fn: (m: any) => void) => (() => void) | undefined;
  onClose: () => void;
}

export default function LegOrderModal({ dark = true, prefill, sendWs, onHedge, sendForBot, subscribe, onClose }: Props) {
  const [price, setPrice] = useState(String(prefill.price || ''));
  // Kapatırken miktar GRUP muhasebesinden geliyor (kullanıcı kararı). Borsadaki
  // gerçek pozisyon farklıysa aşağıda uyarı çıkıyor ama önceliği değiştirmiyoruz.
  const [qty, setQty]     = useState(
    prefill.closing ? String(prefill.openQty || '')
                    : (prefill.availQty ? String(prefill.availQty) : ''));
  const [lev, setLev]     = useState(String(prefill.leverage || 5));
  // Hangi tuşla açıldıysa o kipte başlar: "maker kapat" post-only demek.
  // Fiyat maker tarafından gelip emir taker gitseydi niyet ile emir uyuşmazdı.
  const [taker, setTaker] = useState(!prefill.maker);
  const [busy, setBusy]   = useState(false);
  const [res, setRes]     = useState<{ ok: boolean; text: string } | null>(null);
  // Arb akışı: emirden ÖNCEKİ pozisyon (dolum ölçüsü buna göre), yeni grup no,
  // ve borsadan doğrulanmış dolum miktarı. Hiçbiri SQLite'a bağlı değil.
  const [newGroup, setNewGroup] = useState(!!prefill.counterBotId);
  const [baseQty,  setBaseQty]  = useState<number | null>(null);
  const [groupNo,  setGroupNo]  = useState(0);
  const [filled,   setFilled]   = useState(0);
  const [watching, setWatching] = useState(false);
  const waitingGroup = useRef(false);
  const [resting, setResting] = useState<any[]>([]);   // defterde bekleyen emirler
  const [mkt, setMkt] = useState<{ bid: number; ask: number } | null>(null);
  // Fiyata elle dokunulduysa ASLA üzerine yazma — kullanıcı bilerek girmiştir.
  const priceTouched = useRef(false);
  const levTouched   = useRef(false);
  const [maxLev, setMaxLev] = useState<number | null>(null);
  const waitingQuote = useRef(false);
  // ABONELİK BAYAT CLOSURE tutuyor: aşağıdaki subscribe [subscribe, prefill.botId]
  // ile bir kez kuruluyor, dolayısıyla state'i İLK render'daki haliyle görüyor.
  // baseQty hep null göründüğü için dolum hiç hesaplanmadı; groupNo hep 0 göründüğü
  // için emir grupsuz gitti ve atama hiç planlanmadı. Handler içinde okunan her
  // değer ref üzerinden gitmeli.
  const baseRef  = useRef<number | null>(null);
  const gnoRef   = useRef(0);
  const priceRef = useRef(price);
  const takerRef = useRef(taker);
  useEffect(() => { priceRef.current = price; }, [price]);
  useEffect(() => { takerRef.current = taker; }, [taker]);
  const qtyRef = useRef(qty);
  const levRef = useRef(lev);
  useEffect(() => { qtyRef.current = qty; }, [qty]);
  useEffect(() => { levRef.current = lev; }, [lev]);

  const card  = dark ? 'bg-[#0a1628] border-[#164a9e]' : 'bg-white border-[#d4e0f0]';
  const sub   = dark ? 'bg-[#061530] border-[#0a2560]' : 'bg-[#eef3fa] border-[#d4e0f0]';
  const muted = dark ? 'text-blue-300/70' : 'text-slate-500';
  const inp   = dark ? 'bg-[#03112b] border-[#0a2560] text-blue-50'
                     : 'bg-white border-[#d4e0f0] text-slate-800';

  // Emirden ÖNCEKİ pozisyonu oku — dolumu buna göre ölçeceğiz. Borsadan geliyor,
  // SQLite'a bakmıyoruz: "gerçekten doldu mu" sorusunun tek doğru kaynağı borsa.
  useEffect(() => {
    // Her açılışta iste: başlangıç pozisyonu (dolum ölçüsü), azami kaldıraç,
    // açık emirler ve taze piyasa hepsi tek cevaptan geliyor.
    sendForBot({ type: 'get_leg_quote',
      payload: { botId: prefill.botId, symbol: prefill.symbol } }, prefill.botId);
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  // Emir gönderildikten sonra pozisyonu yokla — dolum onaylanınca tuş belirir.
  useEffect(() => {
    if (!watching) return;
    const id = setInterval(() => sendForBot({ type: 'get_leg_quote',
      payload: { botId: prefill.botId, symbol: prefill.symbol } }, prefill.botId), 2500);
    return () => clearInterval(id);
  }, [watching, prefill.botId, prefill.symbol, sendForBot]);

  useEffect(() => {
    const unsub = subscribe((m: any) => {
      if (m.type === 'leg_quote' && m.bot_id === prefill.botId) {
        const q = m.payload || {};
        setResting(q.open_orders || []);
        // AZAMİ KALDIRAÇ: elle değiştirmediysen paritenin izin verdiği en yükseğe çek.
        const ml = Number(q.max_leverage || 0);
        if (ml > 0) {
          setMaxLev(ml);
          if (!levTouched.current) setLev(String(ml));
        }
        setMkt({ bid: Number(q.best_bid || 0), ask: Number(q.best_ask || 0) });
        // TAZE FİYATLA GÖNDER: popup açıldığı andaki fiyat donuyordu. Bir emir
        // 0.028127'den gönderildi, o ana kadar bid 0.028007'ye düşmüştü — taker
        // niyetiyle atılan emir sessizce maker'a dönüp defterde kaldı.
        if (waitingQuote.current) {
          waitingQuote.current = false;
          const live = prefill.side === 'long' ? Number(q.best_ask || 0) : Number(q.best_bid || 0);
          const cur  = Number(priceRef.current) || 0;
          const use  = (takerRef.current && !priceTouched.current && live > 0) ? live : cur;
          if (use !== cur) setPrice(String(use));
          send(gnoRef.current, use);
          return;
        }
        const sz = Number(q.pos_size || 0);
        const signed = q.pos_side === 'short' ? -sz : sz;
        if (baseRef.current === null) { baseRef.current = signed; setBaseQty(signed); return; }
        // Dolum = pozisyonun EMİR YÖNÜNDE ne kadar değiştiği
        const delta = (signed - baseRef.current) * (prefill.side === 'long' ? 1 : -1);
        if (delta > 1e-12) setFilled(delta);
        return;
      }
      if (m.type === 'lp_group_created') {
        const n = Number(m.payload?.groupNo || 0);
        if (n > 0) {
          gnoRef.current = n;
          setGroupNo(n);
          // Emir grup numarasını BEKLEDİ: groupNo'suz gönderilseydi backend dolumu
          // hiçbir gruba bağlayamazdı. Yerel WS turu birkaç milisaniye.
          if (waitingGroup.current) { waitingGroup.current = false; askQuoteThenSend(n); }
        }
        return;
      }
      if (m.type !== 'manual_order_result') return;
      // bot_id EŞLEŞMELİ: başka bir botun emir sonucu bu popup'ta "başarılı"
      // görünüp yanlış güven verebilirdi.
      if (m.bot_id && m.bot_id !== prefill.botId) return;
      setBusy(false);
      setRes(m.payload?.success
        ? { ok: true, text: 'Emir gönderildi.' }
        : { ok: false, text: m.payload?.error || 'Emir reddedildi' });
    });
    return () => unsub?.();
  }, [subscribe, prefill.botId]);

  const p = Number(price) || 0;
  const q = Number(qty) || 0;
  const notional = p * q;
  const feePct = taker ? (prefill.takerFee ?? 0) : (prefill.makerFee ?? 0);
  const feeUsd = notional * feePct / 100;

  const send = (gno: number, px?: number) => {
    // sendForBot: HEDEF borsaya gönderir. sendWs aktif bota yönlendiriyordu —
    // tarayıcıdaki bacak aktif bot olmak zorunda değil.
    setWatching(true);
    sendForBot({
      type: 'manual_order',
      payload: {
        symbol: prefill.symbol, side: prefill.side,
        // Hepsi REF'ten: send() abonelik closure'ından çağrılıyor, state'i ilk
        // render'daki haliyle görür. Miktarı düzenleyip gönderirsen eski değer giderdi.
        price: px ?? (Number(priceRef.current) || 0),
        quantity: Number(qtyRef.current) || 0,
        leverage: Number(levRef.current) || 5, postOnly: !takerRef.current,
        // Kapatma emri ASLA yeni pozisyon açmamalı: iki kez tetiklenirse borsa
        // ikinciyi reddeder, seni ters yöne sokmaz.
        reduceOnly: !!prefill.closing,
        groupNo: gno || prefill.groupNo || 0,
      },
    }, prefill.botId);
  };

  const fire = () => {
    if (!p || !q) { setRes({ ok: false, text: 'Fiyat ve miktar gerekli.' }); return; }
    setBusy(true); setRes(null);
    // Yeni gruba atanacaksa ÖNCE grup açılıp numarası bekleniyor; emir o numarayla
    // gidiyor ki backend dolumu doğrudan gruba bağlasın.
    if (newGroup && !groupNo && sendWs) {
      waitingGroup.current = true;
      // Karşı bacağı GRUPLA BİRLİKTE kaydet: popup kapansa da grup kartındaki
      // "Karşı bacağı tamamla" hedefi hazır bulsun.
      sendWs({ type: 'create_lp_group', payload: {
        kind: 'perp',
        hedgeBot: prefill.counterBotId || '',
        hedgeSymbol: prefill.counterSymbol || '',
      } });
      return;
    }
    askQuoteThenSend(groupNo);
  };

  // Emri TAZE fiyatla gönder: kotasyonu iste, cevap gelince send() çağrılır.
  const askQuoteThenSend = (gno: number) => {
    gnoRef.current = gno;
    waitingQuote.current = true;
    sendForBot({ type: 'get_leg_quote',
      payload: { botId: prefill.botId, symbol: prefill.symbol } }, prefill.botId);
    // Kotasyon gelmezse emir asılı kalmasın — 3 sn sonra elindeki fiyatla gönder.
    setTimeout(() => {
      if (waitingQuote.current) { waitingQuote.current = false; send(gno); }
    }, 3000);
  };

  const isBuy = prefill.side === 'long';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className={`w-[420px] max-w-full rounded-xl border ${card} p-4 shadow-2xl`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3">
          <span className={`px-2 py-0.5 rounded text-xs font-bold ${isBuy
            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
            : 'bg-rose-500/20 text-rose-400 border border-rose-500/40'}`}>
            {isBuy ? 'LONG' : 'SHORT'}
          </span>
          <span className="font-semibold text-sm">{prefill.symbol}</span>
          {prefill.closing && (
            <span className="px-1.5 py-0.5 rounded text-[10px] border border-amber-500/40 bg-amber-500/10 text-amber-300">
              kapat
            </span>
          )}
          <span className={`text-[11px] ${muted}`}>{prefill.botName}</span>
          <button onClick={onClose} className={`ml-auto ${muted} hover:text-rose-400`}><X size={16} /></button>
        </div>

        <div className="grid grid-cols-2 gap-2 text-[11px]">
          <label className="flex flex-col gap-1">
            <span className={muted}>Fiyat ({isBuy ? 'ask' : 'bid'})</span>
            <input value={price}
              onChange={e => { priceTouched.current = true; setPrice(e.target.value); }}
              className={`px-2 py-1.5 rounded-lg border font-mono ${inp}`} />
          </label>
          <label className="flex flex-col gap-1">
            <span className={`${muted} flex items-center gap-1`}>
              Miktar
              {prefill.closing && (prefill.openQty || 0) > 0 && (
                <button onClick={() => setQty(String(prefill.openQty))}
                  className="text-[10px] px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-300 hover:bg-amber-500/15"
                  title="Gruptaki açık miktarın tamamı">
                  tamamı {Number(prefill.openQty).toLocaleString('en-US')}
                </button>
              )}
              {!prefill.closing && prefill.availQty > 0 && (
                <button onClick={() => setQty(String(prefill.availQty))}
                  className="text-[10px] px-1.5 py-0.5 rounded border border-sky-500/40 text-sky-300 hover:bg-sky-500/15"
                  title="O fiyatta görünen teklifin tamamı">
                  tümü {prefill.availQty.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </button>
              )}
            </span>
            <input value={qty} onChange={e => setQty(e.target.value)}
              className={`px-2 py-1.5 rounded-lg border font-mono ${inp}`} />
          </label>
          <label className="flex flex-col gap-1">
            <span className={muted}>
              Kaldıraç{maxLev ? <span className="opacity-60"> · azami {maxLev}x</span> : null}
            </span>
            <input value={lev}
              onChange={e => { levTouched.current = true; setLev(e.target.value); }}
              className={`px-2 py-1.5 rounded-lg border font-mono ${inp}`} />
          </label>
          <label className="flex items-end pb-1">
            <button onClick={() => setTaker(v => !v)}
              className={`px-2.5 py-1.5 rounded-lg border w-full transition-colors ${taker
                ? 'border-amber-500/50 bg-amber-500/15 text-amber-300'
                : 'border-emerald-500/50 bg-emerald-500/15 text-emerald-300'}`}
              title={taker ? 'Karşı tarafı geçer — hemen dolar, taker komisyonu' : 'Deftere yazılır — dolmayabilir, maker komisyonu'}>
              {taker ? 'Taker (hemen)' : 'Maker (post-only)'}
            </button>
          </label>
        </div>

        {prefill.closing && (
          <div className={`mt-3 rounded-lg border ${sub} p-2.5 text-[11px] space-y-1`}>
            <div className="flex justify-between">
              <span className={muted}>Gruptaki açık pozisyon</span>
              <span className="tabular-nums">
                <span className={prefill.openSide === 'long' ? 'text-emerald-400' : 'text-rose-400'}>
                  {String(prefill.openSide || '').toUpperCase()}
                </span>{' '}
                {Number(prefill.openQty || 0).toLocaleString('en-US')}
              </span>
            </div>
            {(prefill.posSize || 0) > 0 &&
             Math.abs((prefill.posSize || 0) - (prefill.openQty || 0)) > 1e-9 && (
              <p className="text-amber-400 flex items-start gap-1.5 pt-0.5">
                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                Borsadaki pozisyon {Number(prefill.posSize).toLocaleString('en-US')}
                {' '}({String(prefill.posSide || '').toUpperCase()}) — grup muhasebesinden farklı.
                Miktar grup değerinden dolduruldu.
              </p>
            )}
            {(prefill.ageS ?? -1) > 10 && (
              <p className="text-amber-400 flex items-start gap-1.5">
                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                Fiyat {Math.round(prefill.ageS as number)} sn eski — bu borsada canlı
                defter yok. Göndermeden önce doğrula.
              </p>
            )}
            <p className={muted}>
              reduce_only açık — bu emir yeni pozisyon açamaz, yalnız kapatır.
            </p>
          </div>
        )}

        <div className={`mt-3 rounded-lg border ${sub} p-2.5 text-[11px] space-y-0.5`}>
          <div className="flex justify-between">
            <span className={muted}>Notional</span>
            <span className="tabular-nums">${notional.toLocaleString('en-US', { maximumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between">
            <span className={muted}>
              Komisyon ({taker ? 'taker' : 'maker'} {feePct.toFixed(3)}%
              {prefill.feeSource !== 'hesap' && <span className="text-amber-400/70"> ~</span>})
            </span>
            <span className="tabular-nums text-rose-400">−${feeUsd.toFixed(4)}</span>
          </div>
        </div>

        {prefill.counterBotId && (
          <div className={`mt-3 rounded-lg border ${sub} p-2.5 text-[11px] space-y-2`}>
            <label className="flex items-center gap-2 cursor-pointer">
              <button onClick={() => setNewGroup(v => !v)} disabled={!!groupNo}
                className={`w-8 h-4 rounded-full relative transition-colors shrink-0 ${newGroup
                  ? 'bg-emerald-500/60' : 'bg-white/15'} disabled:opacity-50`}>
                <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${newGroup ? 'left-4' : 'left-0.5'}`} />
              </button>
              <span>
                Yeni gruba ata
                {groupNo > 0 && <span className="text-emerald-400"> — Grup {groupNo}</span>}
              </span>
            </label>
            <p className={muted}>
              Karşı bacak: <span className="font-mono">{prefill.counterSymbol}</span>
              {' · '}{prefill.counterBotId}
            </p>
            {baseQty === null && (
              <p className={muted}>
                Başlangıç pozisyonu okunuyor… (dolum bunun üzerinden ölçülecek)
              </p>
            )}
            {/* Emrin AKIBETİ. Eskiden yalnız "dolum bekleniyor" yazıyordu: emir
                defterde karşılamadan bekliyorken kullanıcı reddedildiğini sanıyordu. */}
            {watching && resting.length > 0 && (() => {
              const o = resting[0];
              const ref = prefill.side === 'long' ? (mkt?.ask || 0) : (mkt?.bid || 0);
              const away = ref > 0 ? ((o.price - ref) / ref) * 100 : 0;
              return (
                <div className="space-y-0.5">
                  <p className="text-amber-400">
                    Defterde bekliyor — {o.filled.toLocaleString('en-US')} / {o.qty.toLocaleString('en-US')} doldu
                  </p>
                  <p className={muted}>
                    emrin {o.price} · piyasa {ref || '—'}
                    {ref > 0 && (
                      <span className={Math.abs(away) > 0.05 ? 'text-amber-400' : ''}>
                        {' '}({away >= 0 ? '+' : ''}{away.toFixed(2)}% uzakta)
                      </span>
                    )}
                  </p>
                </div>
              );
            })()}
            {watching && filled <= 0 && baseQty !== null && resting.length === 0 && (
              <p className={muted}>Dolum bekleniyor — borsadaki pozisyon izleniyor…</p>
            )}
            {filled > 0 && (
              <button
                onClick={() => onHedge?.({
                  groupNo, quantity: filled, refPrice: Number(price) || 0,
                })}
                className="w-full px-3 py-2 rounded-lg text-xs font-semibold bg-amber-600/25
                  border border-amber-500/50 text-amber-200 hover:bg-amber-600/40">
                Doldu {filled.toLocaleString('en-US', { maximumFractionDigits: 6 })} — karşı bacağı aç
              </button>
            )}
          </div>
        )}

        {res && <p className={`mt-2 text-[11px] break-words ${res.ok ? 'text-emerald-400' : 'text-rose-400'}`}>{res.text}</p>}

        <div className="flex items-center gap-2 mt-3">
          <button onClick={fire} disabled={busy || (!!prefill.counterBotId && baseQty === null)}
            className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-40 ${isBuy
              ? 'bg-emerald-600/25 border border-emerald-500/50 text-emerald-300 hover:bg-emerald-600/40'
              : 'bg-rose-600/25 border border-rose-500/50 text-rose-300 hover:bg-rose-600/40'}`}>
            <Send size={12} /> {busy ? 'Gönderiliyor…'
              : prefill.closing ? `${isBuy ? 'AL' : 'SAT'} — pozisyonu kapatır`
              : `${isBuy ? 'AL' : 'SAT'} — emir gönderir`}
          </button>
          <button onClick={onClose} className={`px-3 py-2 rounded-lg text-xs border ${sub} ${muted}`}>Kapat</button>
        </div>
      </div>
    </div>
  );
}
